/**
 * Tesoreria › Mutui e finanziamenti (09/10/2026): the loans of K-Rev with
 * the banks and from the partners. The instalments come from the bank
 * movements, recognised by the number of the loan written in the
 * description, with capital, interest and costs as the bank splits them;
 * the debt left is counted from a known residual at a date.
 */
import { z } from 'zod';
import { parseIsoDate } from './dates.js';
import { fromCents, toCents } from './money.js';
import { isoDateSchema, optionalText, requiredText } from './validation.js';
export const LOAN_KINDS = ['mutuo', 'finanziamento', 'prestito_socio'];
export const LOAN_KIND_LABELS = {
    mutuo: 'Mutuo',
    finanziamento: 'Finanziamento',
    prestito_socio: 'Prestito socio',
};
/**
 * What a movement is for a loan: an instalment, the money lent, the costs of
 * lending it, or the bank taking an instalment back (3.16).
 */
export const LOAN_MOVEMENT_KINDS = ['rata', 'erogazione', 'spese', 'storno'];
export const LOAN_MOVEMENT_KIND_LABELS = {
    rata: 'Rata',
    erogazione: 'Erogazione',
    spese: 'Spese di erogazione',
    storno: 'Storno della rata',
};
/** How many days after an instalment the bank may reverse it (3.16). */
export const LOAN_REVERSAL_DAYS = 7;
const amountOf = (text) => fromCents(toCents(text.replace(/\./g, '').replace(',', '.')));
/** The number of a loan as it is compared: digits and slashes only. */
export function loanKey(value) {
    return value.replace(/[^\d/]/g, '');
}
/**
 * Whether the number of a loan, as entered, is the one read in a movement:
 * «08/12345678» or «12345678» for Intesa's «00/12345678»; «021/211487» or
 * «021/211487/01» for BCC's «021/211487».
 */
export function loanNumberMatches(number, key) {
    if (!number)
        return false;
    const own = loanKey(number);
    if (!own)
        return false;
    return (own === key ||
        own.replace(/^\d{2}\//, '') === key ||
        own.startsWith(`${key}/`) ||
        key.endsWith(`/${own}`));
}
/**
 * A movement read as a loan movement, or null. Intesa (causale 15): «MUTUO
 * 08/12345678 QUOTA CAPITALE 540,20 QUOTA INTERESSI 16,29 ARRETRATI/ALTRO
 * 0,00» (also «FINANZIAMENTO», prefix 00/); BCC: «Addebito rata mutuo
 * *021/211487/001 Scad.:12/02/2026 Cap.: 2.156,65 Int.: 717,94 Spese: 4,00
 * Altro: 0,00», «Erogazione mutuo *Erogaz. Mutuo 021/211487/01», «Spese
 * erogazione mutuo …».
 */
export function readLoanMovement(description, amount) {
    const text = description.replace(/\s+/g, ' ');
    const intesa = /(?:MUTUO|FINANZIAMENTO) \d{2}\/(\d{5,}) QUOTA CAPITALE ([\d.,]+) QUOTA INTERESSI ([\d.,]+) ARRETRATI\/ALTRO ([\d.,]+)/i.exec(text);
    if (intesa) {
        return {
            key: intesa[1],
            kind: 'rata',
            capital: amountOf(intesa[2]),
            interest: amountOf(intesa[3]),
            fees: amountOf(intesa[4]),
            instalment: null,
        };
    }
    const bcc = /rata mutuo \*?(\d{3}\/\d+)\/(\d{3}) .*?Cap\.: ?([\d.,]+) Int\.: ?([\d.,]+) Spese: ?([\d.,]+) Altro: ?([\d.,]+)/i.exec(text);
    if (bcc) {
        return {
            key: bcc[1],
            kind: 'rata',
            capital: amountOf(bcc[3]),
            interest: amountOf(bcc[4]),
            fees: fromCents(toCents(amountOf(bcc[5])) + toCents(amountOf(bcc[6]))),
            instalment: Number(bcc[2]),
        };
    }
    const granted = /(Spese )?erogazione mutuo \*?Erogaz\. Mutuo (\d{3}\/\d+)\/\d+/i.exec(text);
    if (granted) {
        const value = fromCents(Math.abs(toCents(amount)));
        const costs = Boolean(granted[1]) || toCents(amount) < 0;
        return {
            key: granted[2],
            kind: costs ? 'spese' : 'erogazione',
            capital: costs ? '0.00' : value,
            interest: '0.00',
            fees: costs ? value : '0.00',
            instalment: null,
        };
    }
    return null;
}
/**
 * The bank took instalments back, for lack of funds (3.16): an instalment
 * reversed and its reversal cancel out, only the debits after them count. Both
 * stay among the movements of the loan, in the order of the dates.
 */
export function applyLoanReversals(payments, reversals) {
    const result = [];
    for (const payment of payments) {
        const reversal = reversals.find((item) => item.reversedMovementId === payment.movementId);
        if (!reversal) {
            result.push(payment);
            continue;
        }
        result.push({
            ...payment,
            capital: '0.00',
            interest: '0.00',
            fees: '0.00',
            reversed: {
                on: reversal.date,
                capital: payment.capital,
                interest: payment.interest,
                fees: payment.fees,
            },
        }, {
            movementId: reversal.movementId,
            accountCode: reversal.accountCode,
            date: reversal.date,
            kind: 'storno',
            amount: reversal.amount,
            capital: '0.00',
            interest: '0.00',
            fees: '0.00',
            instalment: null,
            manual: false,
            reversed: null,
            residualAfter: null,
        });
    }
    // Stable: on the same day a reversal stays after its instalment.
    return result.sort((a, b) => a.date.localeCompare(b.date));
}
/**
 * The debt left at a date from a known residual at another date: the capital
 * repaid between the two goes back or forward, the money lent the other way.
 * Null without a known residual.
 */
export function loanResidual(anchor, payments, date) {
    if (!anchor)
        return null;
    let cents = toCents(anchor.residual);
    for (const payment of payments) {
        // A movement on the day of the anchor is already in it; from the amount lent, only the
        // money lent that day is (2.21): a repayment on the day of the loan comes after it.
        const later = anchor.lent && payment.kind !== 'erogazione' && payment.date === anchor.date;
        const between = date < anchor.date
            ? payment.date > date && payment.date <= anchor.date && !later
            : (payment.date > anchor.date || later) && payment.date <= date;
        if (!between)
            continue;
        const change = toCents(payment.capital) * (payment.kind === 'erogazione' ? -1 : 1);
        cents += date < anchor.date ? change : -change;
    }
    return fromCents(Math.max(cents, 0));
}
/**
 * The plan from the rows of a sheet (columns rata, scadenza, totale,
 * interessi, capitale, spese, residuo; Italian or plain numbers, dates
 * dd/mm/yyyy or yyyy-mm-dd). Rows that cannot be read are problems.
 */
export function readLoanSchedule(rows) {
    const problems = [];
    const lines = [];
    // The column that starts with the name, otherwise one that contains it («Quota capitale»).
    const pick = (row, name) => {
        const entries = Object.entries(row).map(([key, value]) => [key.trim().toLowerCase(), value]);
        return (entries.find(([key]) => key.startsWith(name)) ??
            entries.find(([key]) => key.includes(name)))?.[1];
    };
    const money = (value) => {
        if (typeof value === 'number')
            return fromCents(Math.round(value * 100));
        const text = String(value ?? '').trim();
        if (!text)
            return null;
        try {
            return fromCents(toCents(text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text));
        }
        catch {
            return null;
        }
    };
    const day = (value) => {
        const text = String(value ?? '').trim();
        const italian = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text);
        const iso = italian
            ? `${italian[3]}-${italian[2].padStart(2, '0')}-${italian[1].padStart(2, '0')}`
            : text.slice(0, 10);
        try {
            parseIsoDate(iso);
            return iso;
        }
        catch {
            return null;
        }
    };
    rows.forEach((row, index) => {
        const instalment = Number(pick(row, 'rata'));
        const dueDate = day(pick(row, 'scadenza'));
        const values = ['totale', 'interessi', 'capitale', 'spese', 'residuo'].map((name) => money(pick(row, name)));
        if (!Number.isInteger(instalment) || instalment <= 0 || !dueDate || values.includes(null)) {
            problems.push(`riga ${index + 2}`);
            return;
        }
        const [total, interest, capital, fees, residual] = values;
        lines.push({
            instalment,
            dueDate,
            total: total,
            interest: interest,
            capital: capital,
            fees: fees,
            residual: residual,
        });
    });
    return { lines: lines.sort((a, b) => a.instalment - b.instalment), problems };
}
const money = z
    .string()
    .trim()
    .regex(/^\d{1,12}(\.\d{1,2})?$/, { message: 'Importo non valido' });
/** Body of POST /api/treasury/loans and PATCH /api/treasury/loans/:id. */
export const loanSchema = z.object({
    kind: z.enum(LOAN_KINDS),
    name: requiredText(120),
    /** The account of the bank it is paid from (code of the table «Conti»). */
    accountCode: z.string().trim().min(1).max(30),
    lender: optionalText(120),
    /** The number of the loan, as the bank writes it: the movements are recognised by it. */
    number: optionalText(40),
    amount: money.nullable(),
    startDate: isoDateSchema.nullable(),
    endDate: isoDateSchema.nullable(),
    instalments: z.number().int().min(1).max(600).nullable(),
    /** A known debt left at a date: from the bank (Affidamenti) or the amount lent. */
    anchorDate: isoDateSchema.nullable(),
    anchorResidual: money.nullable(),
    rate: optionalText(120),
    notes: optionalText(2000),
    active: z.boolean().default(true),
    /** The subject it is with (2.22), for a partner's loan. */
    subjectId: z.uuid().nullable().optional(),
});
export const loanScheduleImportSchema = z.object({
    file: z.string().min(1).max(5_000_000),
    fileName: z.string().trim().min(1).max(200),
});
/**
 * The instalments due in a period after the last one paid: from the plan,
 * otherwise the last instalment again each month up to the end of the loan.
 */
export function futureInstalments(input) {
    const planned = input.schedule.filter((line) => line.dueDate > input.from && line.dueDate <= input.to);
    if (input.schedule.length) {
        return planned.map((line) => ({ date: line.dueDate, amount: line.total, estimated: false }));
    }
    if (!input.last)
        return [];
    const result = [];
    const day = Number(input.last.date.slice(8, 10));
    let year = Number(input.last.date.slice(0, 4));
    let month = Number(input.last.date.slice(5, 7));
    for (let index = 0; index < 600; index += 1) {
        month += 1;
        if (month > 12) {
            month = 1;
            year += 1;
        }
        const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
        const date = `${year}-${String(month).padStart(2, '0')}-${String(Math.min(day, last)).padStart(2, '0')}`;
        if (date > input.to || (input.endDate && date > input.endDate))
            break;
        if (date > input.from) {
            result.push({
                date,
                amount: fromCents(Math.abs(toCents(input.last.amount))),
                estimated: true,
            });
        }
    }
    return result;
}
//# sourceMappingURL=loans.js.map